import { call, takeLatest, select, put } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { makeBookingManagerApi, IBookingManagerOverviewViewResponse } from 'services/BookingManagerApi';
import {
  GET_BOOKING_MANAGER_OVERVIEW_REQUEST,
  GetBookingManagerOverviewRequestAction,
  getBookingManagerOverviewSuccessAction,
  getBookingManagerOverviewFailureAction,
  getSpecialRequestsSuccessAction,
  getSpecialRequestsRequestAction,
  getBookingTypeSuccessAction,
  getBookingTypeRequestAction,
} from '../actions';
import * as BreakdownActions from '../../breakdown/actions';
import { getGuestInformationSuccessAction, getGuestInformationRequestAction } from '../../guestInformation/actions';
import { getBookingCommentsSuccessAction, getBookingCommentsRequestAction } from '../../comments/actions';
import { getChecklistSuccessAction, getChecklistRequestAction } from '../../checklist/actions';
import {
  getFinanceDocumentSuccessAction,
  getFinanceDocumentRequestAction,
  getFinanceDocumentBalanceSuccessAction,
  getFinanceDocumentBalanceRequestAction,
} from '../../finance/actions';
import { toFrontendBreakdownWithExchangeRate } from '../../breakdown/sagas/saga';

import { bookingUuidSelector, bookingSelector } from '../../../selectors';

/**
 * Loads the `booking-manager-overview` BMS view - a single request that returns the same
 * data as the individual microdocument endpoints, each key in the exact shape of its
 * individual endpoint - and fans the response out into the existing success actions, so
 * all the reducers/selectors keep working unchanged.
 *
 * If the view request fails, we degrade gracefully by dispatching the individual request
 * actions instead (the pre-view behaviour).
 */
export function* getBookingManagerOverviewSaga(action: GetBookingManagerOverviewRequestAction) {
  const bookingManagerApi = makeBookingManagerApi();
  const bookingUuid = yield select(bookingUuidSelector);

  try {
    const result: AxiosResponse<IBookingManagerOverviewViewResponse> = yield call(
      bookingManagerApi.getBookingManagerOverview,
      bookingUuid
    );
    const overview = result.data;

    // keys the user has no read permission for are omitted by the backend
    if (overview.guestDetails) {
      yield put(getGuestInformationSuccessAction(overview.guestDetails.guests));
    }
    if (overview.cancellationPolicies) {
      yield put(
        BreakdownActions.getCancellationPoliciesSuccessAction(overview.cancellationPolicies.cancellationPolicies)
      );
    }
    if (overview.paymentTerms) {
      yield put(BreakdownActions.getPaymentTermsSuccessAction(overview.paymentTerms.paymentTerms));
    }
    if (overview.offerTerms) {
      yield put(BreakdownActions.getOfferTermsSuccessAction(overview.offerTerms.offerTerms));
    }
    if (overview.policiesAndRestrictions) {
      yield put(
        BreakdownActions.getPoliciesAndRestrictionsSuccessAction(
          overview.policiesAndRestrictions.policiesAndRestrictions
        )
      );
    }
    if (overview.specialRequests) {
      yield put(getSpecialRequestsSuccessAction(overview.specialRequests.specialRequests));
    }
    if (overview.comments) {
      yield put(getBookingCommentsSuccessAction(overview.comments.comments));
    }
    if (overview.checklist) {
      yield put(getChecklistSuccessAction(overview.checklist.checklistItems));
    }
    if (overview.bookingType) {
      yield put(getBookingTypeSuccessAction(overview.bookingType.bookingType));
    }
    // the Overview page always renders the SALES finance table (the container sets
    // financeTableTypeToRender to SALES on mount), so the sales document fans out into
    // the same success actions the finance sagas would have dispatched
    if (overview.financeSales) {
      yield put(getFinanceDocumentSuccessAction({ rows: overview.financeSales.rows }));
    }
    if (overview.financeSalesBalance) {
      yield put(
        getFinanceDocumentBalanceSuccessAction({
          totalCreditCents: overview.financeSalesBalance.totalCreditCents,
          totalDebitCents: overview.financeSalesBalance.totalDebitCents,
          totalOutstandingCents: overview.financeSalesBalance.totalOutstandingCents,
        })
      );
    }
    // the version list and headline document are bundled in the view so the page doesn't
    // have to make the sequential versions -> document round trips. the selected version is
    // set with the SILENT action so the version-selection sagas don't re-fetch the documents
    // we already have
    if (overview.headlineBreakdownVersionList) {
      const latestVersion = overview.headlineBreakdownVersionList[0];
      yield put(BreakdownActions.getHeadlineBreakdownVersionListSuccessAction(overview.headlineBreakdownVersionList));
      yield put(BreakdownActions.setHeadlineBreakdownSelectedVersionSilentAction(latestVersion));
      yield put(BreakdownActions.setHeadlineBreakdownRealLatestVersionAction(latestVersion));
    }
    if (overview.headlineLineItemBreakdown) {
      const bookingItem = yield select(bookingSelector);
      yield put(
        BreakdownActions.getHeadlineLineItemBreakdownSuccessAction(
          toFrontendBreakdownWithExchangeRate(overview.headlineLineItemBreakdown, bookingItem?.createdAt),
          overview.headlineLineItemBreakdown.updatedAt
        )
      );
      yield put(BreakdownActions.clearAllSupplierValidationErrorsAction());
    }

    yield put(getBookingManagerOverviewSuccessAction());
  } catch (e) {
    yield put(getBookingManagerOverviewFailureAction(e));

    // graceful degradation - fall back to the individual requests the view replaces
    yield put(getGuestInformationRequestAction());
    yield put(BreakdownActions.getCancellationPoliciesRequestAction());
    yield put(BreakdownActions.getPaymentTermsRequestAction());
    yield put(BreakdownActions.getOfferTermsRequestAction());
    yield put(BreakdownActions.getPoliciesAndRestrictionsRequestAction());
    yield put(getSpecialRequestsRequestAction());
    yield put(getBookingCommentsRequestAction());
    yield put(getFinanceDocumentRequestAction());
    yield put(getFinanceDocumentBalanceRequestAction());
    // restores the versions -> document chain the view replaces
    yield put(BreakdownActions.getHeadlineBreakdownVersionListRequestAction());
    if (action.fallbacks.withChecklist) {
      yield put(getChecklistRequestAction());
    }
    if (action.fallbacks.withBookingType) {
      yield put(getBookingTypeRequestAction());
    }
  }
}

export function* watchGetBookingManagerOverviewSaga() {
  yield takeLatest(GET_BOOKING_MANAGER_OVERVIEW_REQUEST, getBookingManagerOverviewSaga);
}
